"use client";

import { useCallback, useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { mapProjectMilestone, mapProjectSource } from "@/lib/storeMappers";
import type { ProjectMilestone, ProjectSource } from "@/lib/types";
import type { Database } from "@/types/database";

export type ProjectActivityRow = Pick<
  Database["public"]["Tables"]["workspace_activity"]["Row"],
  | "id"
  | "workspace_id"
  | "project_id"
  | "actor_id"
  | "action"
  | "entity_type"
  | "entity_id"
  | "entity_identifier"
  | "created_at"
>;

/**
 * Small project-command-center read model. Core entities still live in the
 * Zustand store; only repository mappings and the indexed project audit stream
 * are loaded here. Both tables are realtime so a Claude Tag sync appears in an
 * already-open project without a reload.
 */
export function useProjectCommandData({
  workspaceId,
  projectId,
}: {
  workspaceId: string;
  projectId?: string | null;
}) {
  const [sources, setSources] = useState<ProjectSource[]>([]);
  const [milestones, setMilestones] = useState<ProjectMilestone[]>([]);
  const [activity, setActivity] = useState<ProjectActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setSources([]);
      setMilestones([]);
      setActivity([]);
      setLoading(false);
      return;
    }

    const sb = createClient();
    let sourceQuery = sb
      .from("project_sources")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("is_primary", { ascending: false })
      .order("repository", { ascending: true });
    if (projectId) sourceQuery = sourceQuery.eq("project_id", projectId);

    let milestoneQuery = sb
      .from("project_milestones")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("target_date", { ascending: true })
      .order("position", { ascending: true });
    if (projectId) milestoneQuery = milestoneQuery.eq("project_id", projectId);

    const activityQuery = projectId
      ? sb
          .from("workspace_activity")
          .select(
            "id,workspace_id,project_id,actor_id,action,entity_type,entity_id,entity_identifier,created_at"
          )
          .eq("workspace_id", workspaceId)
          .eq("project_id", projectId)
          .order("created_at", { ascending: false })
          .limit(250)
      : null;

    const [sourceResult, milestoneResult, activityResult] = await Promise.all([
      sourceQuery,
      milestoneQuery,
      activityQuery ?? Promise.resolve({ data: [], error: null }),
    ]);

    const firstError = sourceResult.error ?? milestoneResult.error ?? activityResult.error;
    setError(firstError?.message ?? null);
    setSources((sourceResult.data ?? []).map(mapProjectSource));
    setMilestones((milestoneResult.data ?? []).map(mapProjectMilestone));
    setActivity((activityResult.data ?? []) as ProjectActivityRow[]);
    setLoading(false);
  }, [projectId, workspaceId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!workspaceId) return;
    const sb = createClient();
    const channel = sb.channel(`project-command:${workspaceId}:${projectId ?? "all"}`);
    const ping = () => void load();

    (channel as unknown as { on: (...args: unknown[]) => typeof channel }).on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "project_sources",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      ping
    );
    (channel as unknown as { on: (...args: unknown[]) => typeof channel }).on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "project_milestones",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      ping
    );
    if (projectId) {
      (channel as unknown as { on: (...args: unknown[]) => typeof channel }).on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "workspace_activity",
          filter: `project_id=eq.${projectId}`,
        },
        ping
      );
    }

    channel.subscribe();
    return () => {
      sb.removeChannel(channel);
    };
  }, [load, projectId, workspaceId]);

  const createMilestone = useCallback(
    async (input: {
      title: string;
      description?: string;
      targetDate: number;
      status?: ProjectMilestone["status"];
      linkedIssueIds?: string[];
    }) => {
      if (!projectId) throw new Error("A project is required to create a milestone.");
      const sb = createClient();
      const { data, error: insertError } = await sb
        .from("project_milestones")
        .insert({
          workspace_id: workspaceId,
          project_id: projectId,
          title: input.title.trim(),
          description: input.description?.trim() ?? "",
          target_date: toDateValue(input.targetDate),
          status: input.status ?? "planned",
          linked_issue_ids: input.linkedIssueIds ?? [],
          position: milestones.length,
        })
        .select()
        .single();
      if (insertError) {
        setError(insertError.message);
        throw insertError;
      }
      const milestone = mapProjectMilestone(data);
      setMilestones((current) => [...current, milestone].sort(compareMilestones));
      return milestone;
    },
    [milestones.length, projectId, workspaceId]
  );

  const updateMilestone = useCallback(
    async (id: string, patch: Partial<ProjectMilestone>) => {
      const dbPatch: Database["public"]["Tables"]["project_milestones"]["Update"] = {};
      if (patch.title !== undefined) dbPatch.title = patch.title.trim();
      if (patch.description !== undefined) dbPatch.description = patch.description.trim();
      if (patch.targetDate !== undefined) dbPatch.target_date = toDateValue(patch.targetDate);
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.position !== undefined) dbPatch.position = patch.position;
      if (patch.linkedIssueIds !== undefined) dbPatch.linked_issue_ids = patch.linkedIssueIds;
      const sb = createClient();
      const { data, error: updateError } = await sb
        .from("project_milestones")
        .update(dbPatch)
        .eq("id", id)
        .eq("workspace_id", workspaceId)
        .select()
        .single();
      if (updateError) {
        setError(updateError.message);
        throw updateError;
      }
      const milestone = mapProjectMilestone(data);
      setMilestones((current) =>
        current.map((item) => (item.id === id ? milestone : item)).sort(compareMilestones)
      );
      return milestone;
    },
    [workspaceId]
  );

  const deleteMilestone = useCallback(
    async (id: string) => {
      const sb = createClient();
      const { error: deleteError } = await sb
        .from("project_milestones")
        .delete()
        .eq("id", id)
        .eq("workspace_id", workspaceId);
      if (deleteError) {
        setError(deleteError.message);
        throw deleteError;
      }
      setMilestones((current) => current.filter((item) => item.id !== id));
    },
    [workspaceId]
  );

  return {
    sources,
    milestones,
    activity,
    loading,
    error,
    refresh: load,
    createMilestone,
    updateMilestone,
    deleteMilestone,
  };
}

function toDateValue(value: number) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function compareMilestones(a: ProjectMilestone, b: ProjectMilestone) {
  return a.targetDate - b.targetDate || a.position - b.position;
}
